import type {LocalizedText} from '../api/types';
import {LOCALIZED_FIELD, type Language, type MessageParams} from './keys';

/** Replaces `{name}` with `params.name`. An unknown placeholder is left visible. */
export function interpolate(template: string, params?: MessageParams): string {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (whole, key: string) => {
    const value = params[key];
    return value === undefined ? whole : String(value);
  });
}

/**
 * Read a `LocalizedText` in the guest's language.
 *
 * **Thai is required by the contract and is the fallback**, because a menu with
 * only English is a broken menu and the Thai field is the one guaranteed to be
 * there. An empty string counts as absent — a merchant who saved a blank English
 * name should get the Thai dish name, not a blank row on the menu.
 */
export function localized(text: LocalizedText | null | undefined, language: Language): string {
  if (!text) return '';
  const value = text[LOCALIZED_FIELD[language]];
  return typeof value === 'string' && value.trim() !== '' ? value : text.th;
}

/**
 * Format integer satang as Thai baht.
 *
 * The rules are the brand's, not a locale's (foundations §8): always the `฿`
 * symbol, **satang shown only when non-zero**, tabular figures (a CSS concern,
 * set on the money style). `฿125` and `฿125.50`, never `฿125.00`.
 *
 * Grouping is western (`฿1,250`) in all three languages: this is a price on a
 * bill, and the POS, the KDS and the printed receipt all render it the same way.
 * A guest comparing the phone with the paper must see the same number.
 */
export function formatSatang(satang: number): string {
  const negative = satang < 0;
  const absolute = Math.abs(Math.trunc(satang));
  const baht = Math.floor(absolute / 100);
  const remainder = absolute % 100;
  const grouped = baht.toLocaleString('en-US');
  const fraction = remainder === 0 ? '' : `.${String(remainder).padStart(2, '0')}`;
  return `${negative ? '-' : ''}฿${grouped}${fraction}`;
}

/**
 * A price **difference**, with its sign in front of the symbol: `+฿10`, `−฿5`.
 *
 * Modifier deltas are the only signed money on this surface, and they are the
 * one place a guest reads a number as an adjustment rather than an amount — "no
 * rice, five baht off". Two things about it are deliberate:
 *
 *   * **The sign leads.** The design prototype composed this as
 *     `(d > 0 ? '+' : '') + money(d)` and rendered `฿-5`: a minus *inside* the
 *     amount, after the currency symbol. That reads as a strange price rather
 *     than a discount, and in a right-aligned column of deltas the signs do not
 *     line up. Formatting the sign separately fixes both.
 *   * **`−` is U+2212, not a hyphen.** At 16px on a cheap panel a hyphen-minus
 *     is a third the width of the plus it sits above and reads as a dash. The
 *     true minus matches the plus, and `.pokpok-money`'s tabular figures keep
 *     the columns square.
 *
 * Zero renders unsigned — a modifier that costs nothing says `฿0`, not `+฿0`.
 */
export function formatSignedSatang(satang: number): string {
  const rounded = Math.trunc(satang);
  const magnitude = formatSatang(Math.abs(rounded));
  if (rounded > 0) return `+${magnitude}`;
  if (rounded < 0) return `−${magnitude}`;
  return magnitude;
}

/**
 * Basis points as a percentage for display: `700` → `7`, `725` → `7.25`.
 * Feeds `{rate}` in the VAT and service-charge strings.
 */
export function formatBasisPoints(bps: number): string {
  const percent = bps / 100;
  return Number.isInteger(percent) ? String(percent) : String(Number(percent.toFixed(2)));
}

const TIME_LOCALE: Record<Language, string> = {
  th: 'th-TH',
  en: 'en-GB',
  zh: 'zh-Hans',
};

/**
 * The zone a time is rendered in when the venue's own has not arrived yet.
 *
 * **`''` — the empty string, meaning "do not render a time at all".** Not the
 * device's zone, and no longer `Asia/Bangkok`.
 *
 * Every time a guest sees on this surface belongs to the venue — "เปิดอีกครั้ง
 * 18:00" is the venue's door, "สั่งเมื่อ 12:30" is the venue's kitchen — and
 * the device is the one clock in the room with no claim to any of them. A
 * tourist on Central European time is the ordinary case on a QR ordering
 * surface, not the exotic one: their phone renders a Bangkok closure as `13:00`
 * and sends them back five hours early to a shut door.
 *
 * `Asia/Bangkok` was pinned here, with a comment saying it stood in for a
 * contract field that did not exist. It was right about every merchant that can
 * exist today (THB only, PromptPay, Thai e-Tax; one zone; no DST) and wrong as a
 * shape: a constant that is exact by accident is a constant nobody revisits.
 * **Escalation C-4 was approved on 2026-08-09 (D-023)** and `ServiceStatus` now
 * carries `time_zone`, so the zone is read rather than assumed.
 *
 * A blank while it is in flight, rather than a guess: a time that appears a beat
 * late is a smaller failure than a time that is confidently five hours wrong.
 */
export const ZONE_UNKNOWN = '';

/**
 * A wall-clock time for a guest — "18:45".
 *
 * 24-hour in all three languages on purpose: it is the format on the venue's
 * printed opening hours and on the till, and an am/pm reading of a closing time
 * is the one misreading that sends someone to a shut door.
 *
 * In the merchant's zone, always. `timeZone` is a REQUIRED parameter with no
 * default, because omitting `timeZone` on `Intl.DateTimeFormat` does not mean
 * "no timezone" — it means the device's, which is the one answer that is wrong
 * for the guest this surface was written for. Making it required means a new
 * call site has to decide, rather than inherit that answer by writing nothing.
 *
 * Empty when the zone is not known yet ([`ZONE_UNKNOWN`]) — see there.
 */
export function formatTime(iso: string, language: Language, timeZone: string): string {
  if (!timeZone) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat(TIME_LOCALE[language], {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone,
  }).format(date);
}

/** Whole minutes since `iso`, floored at zero. Q14's "{minutes} นาที". */
export function minutesSince(iso: string, now: number = Date.now()): number {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return 0;
  return Math.max(0, Math.floor((now - then) / 60_000));
}
